import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import MenuList from '../../components/menu/menu-list/menu-list';
import CategoryList from '../../components/menu/category-list/category-list';
import ProductList from '../../components/menu/product-list/product-list';
import ItemList from '../../components/menu/item-list/item-list';
import OptionGroupList from '../../components/menu/option-group-list/option-group-list';
import {
  ChevronLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Badge, Dropdown } from 'antd';
import { Tooltip } from 'react-tooltip';
import Modifiers from './components/Modifiers';
import productApi from '../../services/productApi';
import NeedsReviewBanner from '../../components/menu/needs-review-banner';
import useSquareIntegrated from '../../components/menu/square-shared/use-square-integrated';
import SquareReconnectStrip from '../../components/menu/square-shared/reconnect-strip';
import SquareSyncActivityBand from '../../components/menu/square-shared/sync-activity-band';
import SquareReportsModal from '../../components/menu/square-shared/square-reports-modal';
import NeedsReviewModal from '../../components/menu/square-shared/needs-review-modal';
import useNeedsReview from '../../components/menu/square-shared/use-needs-review';
import canShowNeedsReviewButton from '../../components/menu/square-shared/needs-review-visibility';
import { parseMenuDeepLink } from '../../components/menu/square-shared/menu-deep-link';
import { buildSelectionParams } from '../../components/menu/square-shared/menu-url-state';
import { readPlatformFlags } from '../../components/menu/product-list/platform-visibility';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const MOBILE_VIEW = {
  CATEGORIES: 'categories',
  PRODUCTS: 'products',
  DETAILS: 'details',
};

const DETAIL_TAB = {
  ITEMS: 'items',
  MODIFIERS: 'modifiers',
  AVAILABILITY: 'availability',
};

const MOBILE_VIEW_META = {
  [MOBILE_VIEW.CATEGORIES]: {
    step: 'Step 1',
    short: 'Categories',
    title: 'Choose Category',
  },
  [MOBILE_VIEW.PRODUCTS]: {
    step: 'Step 2',
    short: 'Products',
    title: 'Choose Product',
  },
  [MOBILE_VIEW.DETAILS]: {
    step: 'Step 3',
    short: 'Details',
    title: 'Manage Details',
  },
};

const getMobileView = (selected) => {
  if (selected.product?.id) return MOBILE_VIEW.DETAILS;
  if (selected.category?.id) return MOBILE_VIEW.PRODUCTS;
  return MOBILE_VIEW.CATEGORIES;
};

const PanelHeading = ({ step, title, description }) => {
  return (
    <div className="border-b border-slate-200/90 bg-gradient-to-r from-white to-slate-50/90 px-4 py-3 dark:border-slate-700 dark:from-slate-800 dark:to-slate-800/70">
      <div className="flex items-center gap-2">
        <span className="inline-flex rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {step}
        </span>
        <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
      </div>
      <p className="mt-1.5 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
        {description}
      </p>
    </div>
  );
};

const EmptyState = ({ title, description }) => {
  return (
    <div className="flex h-full min-h-[14rem] items-center justify-center px-6 py-10">
      <div className="text-center">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>
    </div>
  );
};

const AvailabilityRow = ({ label, value, statusColor = '' }) => {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
      <p className="text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={classNames(
          statusColor || 'text-slate-900 dark:text-slate-100',
          'mt-1 text-sm font-semibold',
        )}
      >
        {value}
      </p>
    </div>
  );
};

const getProductAvailabilityMeta = (product, itemCountOverride) => {
  const itemCount =
    typeof itemCountOverride === 'number'
      ? itemCountOverride
      : (product?.items || []).length;
  const hasNoItems = itemCount === 0;
  const hasActiveHideDate =
    product?.hidedate && new Date() < new Date(product.hidedate);
  const isClosed =
    product?.isdisabled || !product?.visible || hasActiveHideDate;

  return {
    itemCount,
    hasNoItems,
    hasActiveHideDate,
    isClosed,
    publishState: product?.isdisabled ? 'Draft' : 'Published',
    visibilityState: product?.visible ? 'Visible' : 'Hidden',
  };
};

const AvailabilityPanel = ({ product, itemCount }) => {
  const {
    itemCount: resolvedItemCount,
    hasNoItems,
    hasActiveHideDate,
    isClosed,
    publishState,
    visibilityState,
  } = getProductAvailabilityMeta(product, itemCount);

  const platformFlags = readPlatformFlags(product?.productsettings);
  const channels = [];
  if (platformFlags.isweb) channels.push('Web');
  if (platformFlags.ismobile) channels.push('Mobile');
  if (platformFlags.iskiosk) channels.push('Kiosk');

  const hideDateText = product?.hidedate
    ? new Date(product.hidedate).toLocaleString()
    : 'Not Scheduled';

  const reasonTags = [
    product?.isdisabled ? 'Not published' : null,
    !product?.visible ? 'Visibility off' : null,
    hasActiveHideDate ? 'Scheduled hide active' : null,
    hasNoItems ? 'No item linked' : null,
  ].filter(Boolean);

  return (
    <div className="h-full overflow-y-auto  py-4 hide-scrollbar">
      <div
        className={classNames(
          isClosed
            ? 'border-rose-300 bg-rose-50 dark:border-rose-500/40 dark:bg-rose-900/20'
            : 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-900/20',
          'mb-4 flex items-center gap-2 rounded-md border px-3 py-2.5',
        )}
      >
        {isClosed ? (
          <ExclamationTriangleIcon className="h-5 w-5 text-rose-600 dark:text-rose-300" />
        ) : (
          <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
        )}
        <div className="min-w-0">
          <p
            className={classNames(
              isClosed
                ? 'text-rose-700 dark:text-rose-200'
                : 'text-emerald-700 dark:text-emerald-200',
              'text-sm font-semibold',
            )}
          >
            {isClosed
              ? 'Product currently unavailable'
              : 'Product currently available'}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-300">
            Status is calculated from publish state, visibility, hide date and
            item links.
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <AvailabilityRow
          label="Overall Status"
          value={isClosed ? 'Closed' : 'Open'}
          statusColor={
            isClosed
              ? 'text-rose-600 dark:text-rose-300'
              : 'text-emerald-600 dark:text-emerald-300'
          }
        />
        <AvailabilityRow label="Publish State" value={publishState} />
        <AvailabilityRow label="Visibility" value={visibilityState} />
        <AvailabilityRow
          label="Hide Date"
          value={hideDateText}
          statusColor={
            hasActiveHideDate ? 'text-rose-600 dark:text-rose-300' : ''
          }
        />
        <AvailabilityRow
          label="Item Count"
          value={`${resolvedItemCount} item${resolvedItemCount > 1 ? 's' : ''}`}
          statusColor={hasNoItems ? 'text-rose-600 dark:text-rose-300' : ''}
        />
        <AvailabilityRow
          label="Channels"
          value={channels.length > 0 ? channels.join(', ') : 'No Channel Tag'}
        />
      </div>

      {reasonTags.length > 0 && (
        <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 dark:border-slate-600 dark:bg-slate-800/60">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
            Unavailability Reasons
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {reasonTags.map((reason) => (
              <span
                key={reason}
                className="inline-flex rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[0.68rem] font-semibold text-rose-700 dark:border-rose-500/40 dark:bg-rose-900/30 dark:text-rose-200"
              >
                {reason}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Menus = () => {
  const [selected, setSelected] = useState({
    menuid: '',
    productid: '',
    itemid: '',
    category: {},
    optiongroupid: '',
    optionid: '',
    product: {},
    menuStoreIds: [],
    isSettingsUpdated: false,
    isCategoryFetch: false,
    onlyDisabled: false,
  });

  const { integrated: squareIntegrated, checked: squareChecked } = useSquareIntegrated();
  const rolepermissions = useSelector((state) => state.authReducer.rolepermissions);
  const canViewSquareReports = !!rolepermissions?.includes('squarecatalog-sync');
  const [showModifiers, setShowModifiers] = useState(false);
  const [showSquareReports, setShowSquareReports] = useState(false);
  const [showNeedsReviewQueue, setShowNeedsReviewQueue] = useState(false);
  const {
    items: needsReviewQueueItems,
    count: needsReviewQueueCount,
    ready: needsReviewQueueReady,
    refresh: refreshNeedsReviewQueue,
  } = useNeedsReview(squareIntegrated && canViewSquareReports);

  // Set by the queue modal's onChanged; when the modal closes after at least
  // one resolve, the editor's category/product lists are refreshed so the
  // shown/kept products don't linger with stale "New from Square"/Closed
  // styling (the resolve itself only updates the server + badges).
  const needsReviewDirtyRef = useRef(false);
  const [activeDetailTab, setActiveDetailTab] = useState(DETAIL_TAB.MODIFIERS);
  const [detailCounts, setDetailCounts] = useState({
    items: 0,
    modifiers: 0,
  });

  const [needsReviewCounts, setNeedsReviewCounts] = useState([]);
  const needsReviewTotal = needsReviewCounts.reduce((sum, c) => sum + c.count, 0);
  const needsReviewByCategory = Object.fromEntries(
    needsReviewCounts.map((c) => [c.categoryid, c.count])
  );

  const loadNeedsReview = async () => {
    if (!selected.menuid) {
      setNeedsReviewCounts([]);
      return;
    }
    try {
      const res = await productApi.needsReview(selected.menuid);
      setNeedsReviewCounts(res.data.data || []);
    } catch (e) {
      setNeedsReviewCounts([]);
    }
  };

  useEffect(() => {
    loadNeedsReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.menuid]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Populated synchronously during render — NOT inside an effect — so it's
  // resolved before any child (MenuList, etc.) mounts. React fires child
  // mount effects before the parent's own effects, so parsing this inside a
  // useEffect here left MenuList's own mount-time getMenuList() call reading
  // a still-null ref for the first fetch. Lazy-init guarded by the
  // `undefined` sentinel keeps this idempotent across StrictMode's double
  // render.
  const deepLinkRef = useRef(undefined);
  if (deepLinkRef.current === undefined) {
    const parsed = parseMenuDeepLink(searchParams);
    deepLinkRef.current = Object.values(parsed).every((v) => v === null)
      ? null
      : parsed;
  }

  // Bumped every time a new in-page deep link is armed (see
  // openProductDeepLink below) so CategoryList/ProductList can re-attempt
  // their preferred-selection logic against data they've already fetched,
  // even when the fetch-triggering deps (menuid / category.id) don't
  // themselves change.
  const [deepLinkNonce, setDeepLinkNonce] = useState(0);

  // Kept in sync every render (not via an effect) so openProductDeepLink —
  // called from click handlers, not render — always reads the current
  // menuid instead of a stale closure value.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // Parent-owned consumption: called by CategoryList/ProductList once they've
  // attempted (matched or not) a preferred selection. Clearing the field here,
  // in the parent, is what makes consumption authoritative — children read it
  // back out as a prop, so even a remount (e.g. the always-mounted mobile
  // tree swapping views) sees null and can never re-apply a stale pref.
  const consumeDeepLink = useCallback((field) => {
    if (deepLinkRef.current) {
      deepLinkRef.current = { ...deepLinkRef.current, [field]: null };
    }
  }, []);

  // In-page equivalent of the URL ?menuid=&categoryid=&productid= deep link,
  // used by the needs-review modal's product links: since that modal renders
  // inside this same route, a router navigate() to the same path is a no-op
  // and the mount-only deepLinkRef above never re-arms. This arms it directly
  // and bumps the nonce so CategoryList/ProductList re-attempt selection.
  const openProductDeepLink = useCallback((target) => {
    deepLinkRef.current = {
      menuid: null,
      categoryid: target.categoryid,
      productid: target.productid,
      optiongroupid: null,
    };
    setDeepLinkNonce((n) => n + 1);
    if (target.menuid && target.menuid !== selectedRef.current.menuid) {
      setSelected((prev) => ({
        ...prev,
        menuid: target.menuid,
        category: {},
        product: {},
        productid: '',
        itemid: '',
        optiongroupid: '',
        optionid: '',
      }));
    }
  }, []);

  // One-shot deep link from the menu editor share/copy-link action:
  // /dashboard/menus?menuid=&categoryid=&productid=&optiongroupid= selects
  // down the tree once each list has fetched (deepLinkRef above is already
  // resolved by the time any of them mount). This effect only handles the
  // optiongroupid side effect: opening Modifiers and stripping that one
  // param so a refresh never reopens it. menuid/categoryid/productid stay in
  // the URL — they're state now, kept in sync by the outbound writer below.
  useEffect(() => {
    if (!deepLinkRef.current) return;
    if (deepLinkRef.current.optiongroupid) {
      setShowModifiers(true);
      const next = new URLSearchParams(searchParams);
      next.delete('optiongroupid');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outbound URL-as-state: mirror the selection into the address bar so any
  // moment's URL is a shareable deep link and refresh restores the view.
  // Guarded off while an inbound deep link still has menuid/categoryid/
  // productid pending — each field on deepLinkRef goes null once
  // MenuList/CategoryList/ProductList have consumed it (matched or missed,
  // see consumeDeepLink above and MenuList's onPreferredConsumed), so this
  // resumes automatically once the staged apply finishes. That's what keeps
  // a mid-flight paste — including a menuid-only "open draft" link — from
  // having its still-unapplied params deleted out from under it while
  // MenuList is still fetching. It also means the writer runs even with an
  // empty selection once nothing is pending — buildSelectionParams then
  // deletes all three keys, which is what cleans stale params out of the
  // URL when the selection empties for good (e.g. the last menu is
  // deleted). searchParams is a dep so an external clobber of the query
  // (e.g. a same-route nav that resets it) retriggers the mirror; the
  // toString() skip below still prevents any write loop, since a write
  // changes searchParams identity but not its serialized value. replace:
  // true keeps the browser back button leaving the page rather than
  // unwinding every click.
  useEffect(() => {
    if (
      deepLinkRef.current &&
      (deepLinkRef.current.menuid ||
        deepLinkRef.current.categoryid ||
        deepLinkRef.current.productid)
    )
      return;
    const next = buildSelectionParams(searchParams, selected);
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.menuid, selected.category?.id, selected.product?.id, searchParams]);

  // Deep link from the sync toast: /dashboard/menus?review=1 opens the
  // queue once the integrated signal and the first queue fetch resolve.
  // The param is stripped either way so refreshes don't reopen the modal.
  useEffect(() => {
    if (searchParams.get('review') !== '1') return;
    if (!squareChecked) return;
    const eligible = squareIntegrated && canViewSquareReports;
    if (eligible && !needsReviewQueueReady) return;
    if (eligible && needsReviewQueueCount > 0) setShowNeedsReviewQueue(true);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, squareChecked, squareIntegrated, canViewSquareReports, needsReviewQueueReady, needsReviewQueueCount]);

  const onFormChange = (value, name) => {
    setSelected((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const hasMenuSelected = Boolean(selected.menuid);
  const hasCategorySelected = Boolean(selected.category?.id);
  const hasProductSelected = Boolean(selected.product?.id);
  const currentMobileView = getMobileView(selected);
  const showMobileBack = currentMobileView !== MOBILE_VIEW.CATEGORIES;

  const mobileViewMeta = useMemo(() => {
    const dynamicTitle =
      currentMobileView === MOBILE_VIEW.DETAILS
        ? selected.product?.name || MOBILE_VIEW_META[currentMobileView].title
        : currentMobileView === MOBILE_VIEW.PRODUCTS
          ? selected.category?.name || MOBILE_VIEW_META[currentMobileView].title
          : MOBILE_VIEW_META[currentMobileView].title;

    return {
      ...MOBILE_VIEW_META[currentMobileView],
      title: dynamicTitle,
    };
  }, [currentMobileView, selected.category?.name, selected.product?.name]);

  useEffect(() => {
    setDetailCounts({
      items: (selected.product?.items || []).length,
      modifiers:
        selected.product?.productoptiongroups?.filter(
          (group) => !group.optiongroup?.isdeleted,
        ).length || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.product?.id]);

  const onMobileBack = () => {
    if (currentMobileView === MOBILE_VIEW.DETAILS) {
      setSelected((prev) => ({
        ...prev,
        product: {},
        productid: '',
        itemid: '',
        optiongroupid: '',
        optionid: '',
      }));
      return;
    }

    if (currentMobileView === MOBILE_VIEW.PRODUCTS) {
      setSelected((prev) => ({
        ...prev,
        category: {},
        product: {},
        productid: '',
        itemid: '',
        optiongroupid: '',
        optionid: '',
      }));
    }
  };

  const modifierCount = detailCounts.modifiers;
  const itemCount = detailCounts.items;
  const availabilityMeta = getProductAvailabilityMeta(
    selected.product,
    itemCount,
  );

  const onItemCountChange = useCallback((count) => {
    setDetailCounts((prev) => ({ ...prev, items: count }));
  }, []);

  const onModifierCountChange = useCallback((count) => {
    setDetailCounts((prev) => ({ ...prev, modifiers: count }));
  }, []);

  const detailTabs = [
    {
      key: DETAIL_TAB.MODIFIERS,
      label: 'Modifiers',
      helper: 'Groups & rules',
      badge: modifierCount,
    },
    {
      key: DETAIL_TAB.AVAILABILITY,
      label: 'Availability',
      helper: 'Visibility status',
      badge: availabilityMeta.isClosed ? 'Closed' : 'Open',
      badgeClassName: availabilityMeta.isClosed
        ? 'border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-500/40 dark:bg-rose-900/30 dark:text-rose-200'
        : 'border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-900/30 dark:text-emerald-200',
    },
  ];

  const renderDetailWorkspace = () => {
    return (
      <div className="flex h-full min-h-0 flex-col p-2 lg:p-3">
        <section
          aria-label="Product items"
          className="mb-2 max-h-[22rem] shrink-0 overflow-y-auto hide-scrollbar rounded-md lg:mb-3"
        >
          <ItemList
            selected={selected}
            setSelected={setSelected}
            onItemCountChange={onItemCountChange}
          />
        </section>

        <div
          role="tablist"
          aria-label="Product detail sections"
          className="mb-2 grid grid-cols-2 gap-1.5 lg:gap-2"
        >
          {detailTabs.map((tab) => {
            const isActive = activeDetailTab === tab.key;

            return (
              <button
                key={tab.key}
                id={`detail-tab-${tab.key}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`detail-panel-${tab.key}`}
                onClick={() => setActiveDetailTab(tab.key)}
                className={classNames(
                  isActive
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-sky-400 dark:bg-slate-700 dark:text-sky-300'
                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600',
                  'rounded-md border px-2 py-1.5 text-left transition lg:rounded-md lg:px-3 lg:py-2',
                )}
              >
                <div className="flex items-center justify-between gap-1">
                  <p className="truncate text-xs font-semibold lg:text-sm">
                    {tab.label}
                  </p>
                  <span
                    className={classNames(
                      tab.badgeClassName ||
                        'border-slate-300 bg-white text-slate-600 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-200',
                      'inline-flex min-w-[1.5rem] items-center justify-center rounded-full border px-1.5 py-px text-[0.6rem] font-semibold lg:min-w-[2rem] lg:px-2 lg:py-0.5 lg:text-[0.68rem]',
                    )}
                  >
                    {tab.badge}
                  </span>
                </div>
                <p className="mt-0.5 hidden truncate text-[0.72rem] text-slate-500 lg:block dark:text-slate-400">
                  {tab.helper}
                </p>
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden rounded-md  border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          {activeDetailTab === DETAIL_TAB.MODIFIERS && (
            <div
              id={`detail-panel-${DETAIL_TAB.MODIFIERS}`}
              role="tabpanel"
              aria-labelledby={`detail-tab-${DETAIL_TAB.MODIFIERS}`}
              className="h-full overflow-y-auto hide-scrollbar"
            >
              <OptionGroupList
                selected={selected}
                setSelected={setSelected}
                onModifierCountChange={onModifierCountChange}
              />
            </div>
          )}
          {activeDetailTab === DETAIL_TAB.AVAILABILITY && (
            <div
              id={`detail-panel-${DETAIL_TAB.AVAILABILITY}`}
              role="tabpanel"
              aria-labelledby={`detail-tab-${DETAIL_TAB.AVAILABILITY}`}
              className="h-full"
            >
              <AvailabilityPanel
                product={selected.product}
                itemCount={itemCount}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative z-0 flex h-[92vh] flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/30">
      <SquareReconnectStrip />
      <SquareSyncActivityBand
        onRefresh={() => {
          refreshNeedsReviewQueue();
          loadNeedsReview();
          // Same reload idiom as switching menus (menu-card.js): drop the
          // category/product selection and flip isCategoryFetch so
          // CategoryList's effect (keyed on [selected.menuid,
          // selected.isCategoryFetch]) refetches. ProductList has no
          // standalone refresh trigger — it refetches from
          // [selected.category.id, selected.onlyDisabled] — so clearing the
          // category selection is what guarantees stale product data isn't
          // left on screen; it's refetched fresh as soon as a category is
          // reselected.
          setSelected((prev) => ({
            ...prev,
            category: {},
            product: {},
            productid: '',
            itemid: '',
            optiongroupid: '',
            optionid: '',
            isCategoryFetch: !prev.isCategoryFetch,
          }));
        }}
      />
      <div className="border-b border-slate-200/80 bg-white/90 backdrop-blur dark:border-slate-600 dark:bg-slate-800/90">
        <div className="flex items-center gap-3 px-3 py-3 lg:justify-between lg:px-4">
          <div className="min-w-0 flex-1">
            <MenuList
              selected={selected}
              setSelected={setSelected}
              preferredMenuId={deepLinkRef.current?.menuid}
              onPreferredConsumed={consumeDeepLink}
            />
          </div>

          {/* Mobile: the four tools collapse into one dropdown so nothing
              overflows off-screen; the trigger carries the review badge so
              the pending-count signal survives the fold. Desktop (lg+) keeps
              the full button row below. */}
          <div className="shrink-0 lg:hidden">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'modifiers',
                    label: 'Modifiers',
                  },
                  ...(squareIntegrated && canViewSquareReports
                    ? [{ key: 'square-reports', label: 'Square Reports' }]
                    : []),
                  ...(canShowNeedsReviewButton({
                    integrated: squareIntegrated,
                    canView: canViewSquareReports,
                    count: needsReviewQueueCount,
                  })
                    ? [
                        {
                          key: 'needs-review',
                          label: `Needs review (${needsReviewQueueCount})`,
                        },
                      ]
                    : []),
                  { type: 'divider' },
                  {
                    key: 'hidden-items',
                    label: selected.onlyDisabled
                      ? 'Hidden items: on'
                      : 'Hidden items: off',
                  },
                ],
                onClick: ({ key }) => {
                  if (key === 'modifiers') setShowModifiers(true);
                  else if (key === 'square-reports') setShowSquareReports(true);
                  else if (key === 'needs-review') setShowNeedsReviewQueue(true);
                  else if (key === 'hidden-items')
                    onFormChange(!selected.onlyDisabled, 'onlyDisabled');
                },
              }}
            >
              <button
                type="button"
                className="flex min-h-[2.5rem] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-100 dark:hover:bg-slate-600"
              >
                <Badge count={needsReviewQueueCount} size="small" offset={[4, -2]}>
                  <AdjustmentsHorizontalIcon className="h-5 w-5" />
                </Badge>
                <span className="text-[0.925rem]">Tools</span>
              </button>
            </Dropdown>
          </div>

          <div className="hidden gap-2 lg:flex lg:w-auto lg:shrink-0 lg:items-center lg:self-stretch">
            <Tooltip id="modifiers" />

            <button
              type="button"
              data-tooltip-id="modifiers"
              data-tooltip-content="You can manage modifiers here"
              onClick={() => setShowModifiers(true)}
              className="flex min-h-[2.5rem] cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-100 dark:hover:bg-slate-600 lg:min-h-[4.5rem] lg:px-4 lg:py-3"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
              <span className="truncate text-[0.925rem]">Modifiers</span>
            </button>

            {/* Everything except Modifiers lives in the Tools dropdown on
                desktop too — one calm button row, the review badge on the
                trigger keeps the pending-count visible. */}
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  ...(squareIntegrated && canViewSquareReports
                    ? [{ key: 'square-reports', label: 'Square Reports' }]
                    : []),
                  ...(canShowNeedsReviewButton({
                    integrated: squareIntegrated,
                    canView: canViewSquareReports,
                    count: needsReviewQueueCount,
                  })
                    ? [
                        {
                          key: 'needs-review',
                          label: `Needs review (${needsReviewQueueCount})`,
                        },
                      ]
                    : []),
                  ...(squareIntegrated && canViewSquareReports
                    ? [{ type: 'divider' }]
                    : []),
                  {
                    key: 'hidden-items',
                    label: selected.onlyDisabled
                      ? 'Hidden items: on'
                      : 'Hidden items: off',
                  },
                ],
                onClick: ({ key }) => {
                  if (key === 'square-reports') setShowSquareReports(true);
                  else if (key === 'needs-review') setShowNeedsReviewQueue(true);
                  else if (key === 'hidden-items')
                    onFormChange(!selected.onlyDisabled, 'onlyDisabled');
                },
              }}
            >
              <button
                type="button"
                className="flex min-h-[2.5rem] cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-100 dark:hover:bg-slate-600 lg:min-h-[4.5rem] lg:px-4 lg:py-3"
              >
                <Badge count={needsReviewQueueCount} size="small" offset={[4, -2]}>
                  <EyeIcon className="h-5 w-5" />
                </Badge>
                <span className="truncate text-[0.925rem]">Tools</span>
              </button>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <div className="hidden h-full min-h-0 p-3 lg:grid lg:grid-cols-12 lg:gap-3">
          <section className="col-span-3 flex min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
            <PanelHeading
              step="Step 1"
              title="Categories"
              description={
                hasMenuSelected
                  ? 'Select a category to continue.'
                  : 'Choose a menu first.'
              }
            />
            <div className="min-h-0 flex-1 overflow-y-auto hide-scrollbar">
              {hasMenuSelected ? (
                <>
                  <NeedsReviewBanner total={needsReviewTotal} />
                  <CategoryList
                    selected={selected}
                    setSelected={setSelected}
                    needsReviewByCategory={needsReviewByCategory}
                    preferredCategoryId={deepLinkRef.current?.categoryid}
                    deepLinkNonce={deepLinkNonce}
                    onPreferredConsumed={consumeDeepLink}
                  />
                </>
              ) : (
                <EmptyState
                  title="Menu required"
                  description="Pick a menu from the top bar to load categories."
                />
              )}
            </div>
          </section>

          <section className="col-span-4 flex min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
            <PanelHeading
              step="Step 2"
              title={hasCategorySelected ? selected.category.name : 'Products'}
              description={
                hasCategorySelected
                  ? 'Select a product to manage items and modifiers.'
                  : 'Select a category to see products.'
              }
            />
            <div className="min-h-0 flex-1 overflow-y-auto hide-scrollbar">
              {hasCategorySelected ? (
                <ProductList
                  selected={selected}
                  setSelected={setSelected}
                  onItemCountChange={onItemCountChange}
                  onProductsChanged={loadNeedsReview}
                  preferredProductId={deepLinkRef.current?.productid}
                  preferredCategoryId={deepLinkRef.current?.categoryid}
                  deepLinkNonce={deepLinkNonce}
                  onPreferredConsumed={consumeDeepLink}
                />
              ) : (
                <EmptyState
                  title="Category required"
                  description="Choose a category from the left panel."
                />
              )}
            </div>
          </section>

          <section className="col-span-5 flex min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
            <PanelHeading
              step="Step 3"
              title={
                hasProductSelected
                  ? selected.product.name
                  : 'Modifiers & Availability'
              }
              description={
                hasProductSelected
                  ? 'Manage modifier groups and availability.'
                  : 'Select a product to manage details.'
              }
            />
            <div className="min-h-0 flex-1">
              {hasProductSelected ? (
                renderDetailWorkspace()
              ) : (
                <EmptyState
                  title="Product required"
                  description="Choose a product from the middle panel."
                />
              )}
            </div>
          </section>
        </div>

        <div className="flex h-full min-h-0 flex-col lg:hidden">
          <div className="border-b border-slate-200 bg-white/90 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/95">
            <div className="flex items-center gap-2">
              {showMobileBack && (
                <button
                  type="button"
                  onClick={onMobileBack}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {mobileViewMeta.title}
                </h2>
              </div>
              <div className="flex items-center gap-1.5">
                {Object.entries(MOBILE_VIEW_META).map(([view], index) => {
                  const isActive = view === currentMobileView;
                  const isComplete =
                    (view === MOBILE_VIEW.CATEGORIES && hasCategorySelected) ||
                    (view === MOBILE_VIEW.PRODUCTS && hasProductSelected);
                  return (
                    <div
                      key={view}
                      className={classNames(
                        isActive
                          ? 'bg-blue-500 dark:bg-sky-400'
                          : isComplete
                            ? 'bg-emerald-400 dark:bg-emerald-500'
                            : 'bg-slate-300 dark:bg-slate-600',
                        'h-1.5 w-6 rounded-full transition-colors',
                      )}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto hide-scrollbar bg-white p-2 dark:bg-slate-900/30">
            {currentMobileView === MOBILE_VIEW.CATEGORIES &&
              (hasMenuSelected ? (
                <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
                  <NeedsReviewBanner total={needsReviewTotal} />
                  <CategoryList
                    selected={selected}
                    setSelected={setSelected}
                    needsReviewByCategory={needsReviewByCategory}
                    preferredCategoryId={deepLinkRef.current?.categoryid}
                    deepLinkNonce={deepLinkNonce}
                    onPreferredConsumed={consumeDeepLink}
                  />
                </div>
              ) : (
                <EmptyState
                  title="Menu required"
                  description="Pick a menu from the top section first."
                />
              ))}

            {currentMobileView === MOBILE_VIEW.PRODUCTS &&
              (hasCategorySelected ? (
                <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
                  <ProductList
                    selected={selected}
                    setSelected={setSelected}
                    onItemCountChange={onItemCountChange}
                    onProductsChanged={loadNeedsReview}
                    preferredProductId={deepLinkRef.current?.productid}
                    preferredCategoryId={deepLinkRef.current?.categoryid}
                    deepLinkNonce={deepLinkNonce}
                    onPreferredConsumed={consumeDeepLink}
                  />
                </div>
              ) : (
                <EmptyState
                  title="Category required"
                  description="Go back and pick a category to continue."
                />
              ))}

            {currentMobileView === MOBILE_VIEW.DETAILS &&
              (hasProductSelected ? (
                <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/90">
                  {renderDetailWorkspace()}
                </div>
              ) : (
                <EmptyState
                  title="Product required"
                  description="Go back and choose a product first."
                />
              ))}
          </div>
        </div>
      </div>

      <Modifiers
        show={showModifiers}
        onClose={() => setShowModifiers(false)}
        preferredGroupId={deepLinkRef.current?.optiongroupid}
      />
      <SquareReportsModal
        open={showSquareReports}
        onClose={() => setShowSquareReports(false)}
      />
      <NeedsReviewModal
        open={showNeedsReviewQueue}
        items={needsReviewQueueItems}
        onClose={() => {
          setShowNeedsReviewQueue(false);
          if (needsReviewDirtyRef.current) {
            needsReviewDirtyRef.current = false;
            // Same reload idiom as the sync band's Refresh: clear the
            // category/product selection and flip isCategoryFetch so both
            // lists refetch — resolved products then render with their
            // fresh visibility instead of stale review styling.
            setSelected((prev) => ({
              ...prev,
              category: {},
              product: {},
              productid: '',
              itemid: '',
              optiongroupid: '',
              optionid: '',
              isCategoryFetch: !prev.isCategoryFetch,
            }));
          }
        }}
        onChanged={() => {
          needsReviewDirtyRef.current = true;
          refreshNeedsReviewQueue();
          loadNeedsReview();
        }}
        onOpenProduct={openProductDeepLink}
      />
    </div>
  );
};

export default Menus;
